import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, FileText, Plus, Trash2, Save, AlertCircle } from 'lucide-react';
import {
  KIT_CATEGORIES,
  STATIONS,
  stationLabel,
  type KitCategory,
  type KitItem,
  type Station,
} from '@hcc/types';
import { useCurrentUser } from '../../hooks/useAuth';
import {
  useCreateKitTemplate,
  useKitSizeFields,
  useKitTemplate,
  useKitTemplatesList,
  useUpdateKitTemplate,
} from '../../hooks/useKitTemplates';
import { Button } from '../../components/ui/Button';
import {
  TemplateTargetPicker,
  targetIsComplete,
  type TemplateTarget,
} from '../../components/admin/TemplateTargetPicker';
import { ApiError } from '../../lib/api';

/**
 * Create or edit a kit template. Same component for both flows;
 * the URL param decides.
 */
export function KitTemplateForm() {
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;
  const navigate = useNavigate();
  const { data: user, isLoading: meLoading } = useCurrentUser();
  const existing = useKitTemplate(isEdit ? (id ?? null) : null);
  const allTemplates = useKitTemplatesList();
  const create = useCreateKitTemplate();
  const update = useUpdateKitTemplate(id ?? '');
  const sizeFields = useKitSizeFields();

  const [target, setTarget] = useState<TemplateTarget>({ role_id: '', category_id: null });
  const [items, setItems] = useState<KitItem[]>([
    { station: 'IT', name: '', quantity: 1, size_required: false },
  ]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (existing.data) {
      setTarget(
        existing.data.category_id
          ? { role_id: null, category_id: existing.data.category_id }
          : { role_id: existing.data.role_id ?? '', category_id: null },
      );
      setItems(
        existing.data.items.length > 0
          ? existing.data.items.map((i) => ({ ...i }))
          : [{ station: 'IT', name: '', quantity: 1, size_required: false }],
      );
    }
  }, [existing.data]);

  if (meLoading) return <main className="p-6 text-sm text-fg-muted">Loading...</main>;
  if (!user) {
    return (
      <main className="p-6 text-sm text-fg-muted">
        Please{' '}
        <Link to="/login" className="underline">
          sign in
        </Link>
        .
      </main>
    );
  }
  const allowed = user.roles.includes('admin') || user.roles.includes('coordinator');
  if (!allowed) {
    return (
      <main className="p-6 text-sm text-fg-muted">
        You do not have permission to manage kit templates.
      </main>
    );
  }

  function setItem(idx: number, patch: Partial<KitItem>) {
    setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  }

  // Link a kit item to a sizing-form row (or clear it). Linking implies the
  // item is sized, and adopts the row's category and canonical name when the
  // item is still unnamed, so a fresh row is filled in with one pick.
  function applySizeLink(idx: number, value: string) {
    if (!value) {
      setItem(idx, { size_ref: undefined });
      return;
    }
    const [form_key, field_key, row_key] = value.split('|');
    const field = (sizeFields.data ?? []).find(
      (f) => f.form_key === form_key && f.field_key === field_key && f.row_key === row_key,
    );
    setItems((prev) =>
      prev.map((it, i) =>
        i === idx
          ? {
              ...it,
              size_ref: { form_key: form_key!, field_key: field_key!, row_key: row_key! },
              size_required: true,
              category: (field?.category as KitCategory | undefined) ?? it.category,
              name: it.name.trim() ? it.name : (field?.item ?? it.name),
            }
          : it,
      ),
    );
  }
  function addItem() {
    setItems((prev) => [...prev, { station: 'IT', name: '', quantity: 1, size_required: false }]);
  }
  function removeItem(idx: number) {
    setItems((prev) => prev.filter((_, i) => i !== idx));
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    const cleanItems = items.filter((i) => i.name.trim().length > 0);
    const payload = {
      role_id: target.role_id || null,
      category_id: target.category_id || null,
      items: cleanItems,
    };
    try {
      if (isEdit) {
        const saved = await update.mutateAsync(payload);
        toast.success(`Saved "${saved.position}"`);
      } else {
        const saved = await create.mutateAsync(payload);
        toast.success(`Created "${saved.position}"`);
      }
      navigate('/admin/kit-templates');
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : 'save failed';
      setError(msg);
      toast.error(msg);
    }
  }

  const pending = create.isPending || update.isPending;

  return (
    <main className="min-h-screen bg-bg text-fg">
      <div className="mx-auto max-w-3xl px-4 py-6 sm:px-6 sm:py-8">
        <Link
          to="/admin/kit-templates"
          className="inline-flex items-center gap-1.5 text-sm text-fg-muted transition-colors hover:text-fg"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to templates
        </Link>

        <header className="mt-3 flex items-start gap-4">
          <div className="flex h-12 w-12 flex-none items-center justify-center rounded-xl bg-accent/10 text-accent">
            <FileText className="h-6 w-6" strokeWidth={2} />
          </div>
          <div>
            <div className="text-xs font-medium uppercase tracking-wider text-accent">
              Kit template
            </div>
            <h1 className="text-2xl font-semibold tracking-tight sm:text-3xl">
              {isEdit ? 'Edit template' : 'New template'}
            </h1>
            <p className="mt-1 text-sm text-fg-muted">
              One template per role or per role category. The list of items here drives the
              per-station kit checklist on the day; a person gets their category&apos;s baseline
              merged with their role&apos;s template, role winning on clashes.
            </p>
          </div>
        </header>

        <form onSubmit={onSubmit} className="mt-6 space-y-6">
          <section className="rounded-lg border border-line/10 bg-surface p-5 shadow-sm sm:p-6">
            <TemplateTargetPicker value={target} onChange={setTarget} />

            {!isEdit && (allTemplates.data?.length ?? 0) > 0 ? (
              <label className="mt-4 block text-sm">
                <span className="font-medium text-fg-muted">
                  Copy items from another template{' '}
                  <span className="font-normal text-fg-dim">(optional)</span>
                </span>
                <select
                  onChange={(e) => {
                    const src = (allTemplates.data ?? []).find((t) => t.id === e.target.value);
                    if (src) {
                      setItems(src.items.map((i) => ({ ...i })));
                      toast.success(`Pre-filled from "${src.position}"`);
                    }
                    e.target.value = '';
                  }}
                  className="mt-1.5 block h-10 w-full rounded-md border border-line-strong/20 bg-surface px-3 text-sm shadow-sm focus:border-accent focus:outline-none focus:ring-2 focus:ring-line/15"
                  defaultValue=""
                >
                  <option value="">Start blank</option>
                  {(allTemplates.data ?? []).map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.position} ({t.items.length} items)
                    </option>
                  ))}
                </select>
                <span className="mt-1 block text-xs text-fg-muted">
                  Pre-fills the items table; you can still edit before saving.
                </span>
              </label>
            ) : null}
          </section>

          <section className="rounded-lg border border-line/10 bg-surface p-5 shadow-sm sm:p-6">
            <div className="flex items-baseline justify-between">
              <h2 className="text-sm font-semibold text-fg-muted">Items</h2>
              <span className="text-xs text-fg-muted">
                {items.length} {items.length === 1 ? 'row' : 'rows'}
              </span>
            </div>

            <div className="mt-3 space-y-2">
              {items.map((it, idx) => (
                <div
                  key={idx}
                  className="grid grid-cols-12 gap-2 rounded-md border border-line/10 bg-surface-2 p-2"
                >
                  <select
                    value={it.station}
                    onChange={(e) => setItem(idx, { station: e.target.value as Station })}
                    aria-label="Station"
                    className="col-span-4 h-9 rounded-md border border-line-strong/20 bg-surface px-2 text-sm shadow-sm focus:border-accent focus:outline-none focus:ring-2 focus:ring-line/15 sm:col-span-2"
                  >
                    {STATIONS.map((s) => (
                      <option key={s} value={s}>
                        {stationLabel(s)}
                      </option>
                    ))}
                  </select>
                  <input
                    type="text"
                    value={it.name}
                    onChange={(e) => setItem(idx, { name: e.target.value })}
                    placeholder="Item name (e.g. Laptop)"
                    className="col-span-8 h-9 rounded-md border border-line-strong/20 bg-surface px-2 text-sm shadow-sm focus:border-accent focus:outline-none focus:ring-2 focus:ring-line/15 sm:col-span-5"
                  />
                  <input
                    type="number"
                    min={1}
                    max={99}
                    value={it.quantity}
                    onChange={(e) => setItem(idx, { quantity: Number(e.target.value) || 1 })}
                    aria-label="Quantity"
                    className="col-span-3 h-9 rounded-md border border-line-strong/20 bg-surface px-2 text-sm shadow-sm focus:border-accent focus:outline-none focus:ring-2 focus:ring-line/15 sm:col-span-2"
                  />
                  {it.station === 'PPE' ? (
                    <select
                      value={it.category ?? ''}
                      onChange={(e) =>
                        setItem(idx, {
                          category: (e.target.value || undefined) as KitCategory | undefined,
                        })
                      }
                      aria-label="Category"
                      className="col-span-7 h-9 rounded-md border border-line-strong/20 bg-surface px-2 text-sm shadow-sm focus:border-accent focus:outline-none focus:ring-2 focus:ring-line/15 sm:col-span-2"
                    >
                      <option value="">PPE (default)</option>
                      {KIT_CATEGORIES.map((c) => (
                        <option key={c} value={c}>
                          {c}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <div className="col-span-7 hidden sm:col-span-2 sm:block" />
                  )}
                  <label className="col-span-7 flex h-9 items-center gap-2 rounded-md border border-line/10 bg-surface px-2 text-xs sm:col-span-2">
                    <input
                      type="checkbox"
                      checked={it.size_required}
                      onChange={(e) => setItem(idx, { size_required: e.target.checked })}
                      className="h-4 w-4 rounded border-line-strong/20"
                    />
                    <span>Size required</span>
                  </label>
                  <button
                    type="button"
                    onClick={() => removeItem(idx)}
                    aria-label="Remove item"
                    className="col-span-2 flex h-9 items-center justify-center rounded-md text-fg-muted transition-colors hover:bg-danger-soft/15 hover:text-danger-soft-fg sm:col-span-1"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>

                  {it.station === 'PPE' ? (
                    <label className="col-span-12 mt-1 flex flex-col gap-1 border-t border-line/10 pt-2 text-xs text-fg-muted sm:flex-row sm:items-center sm:gap-2">
                      <span className="whitespace-nowrap font-medium">Size from sizing form</span>
                      <select
                        value={
                          it.size_ref
                            ? `${it.size_ref.form_key}|${it.size_ref.field_key}|${it.size_ref.row_key}`
                            : ''
                        }
                        onChange={(e) => applySizeLink(idx, e.target.value)}
                        className="h-9 w-full rounded-md border border-line-strong/20 bg-surface px-2 text-sm shadow-sm focus:border-accent focus:outline-none focus:ring-2 focus:ring-line/15"
                      >
                        <option value="">Not linked (fixed item, manual size)</option>
                        {(sizeFields.data ?? []).map((f) => (
                          <option
                            key={`${f.form_key}|${f.field_key}|${f.row_key}`}
                            value={`${f.form_key}|${f.field_key}|${f.row_key}`}
                          >
                            {f.form_title}: {f.item}
                          </option>
                        ))}
                      </select>
                    </label>
                  ) : null}
                </div>
              ))}
              {items.length === 0 ? (
                <div className="rounded-md border border-dashed border-line-strong/20 px-4 py-6 text-center text-sm text-fg-muted">
                  No items yet.
                </div>
              ) : null}
            </div>

            <Button
              type="button"
              variant="secondary"
              size="sm"
              iconLeft={<Plus className="h-3.5 w-3.5" />}
              onClick={addItem}
              className="mt-3"
            >
              Add item
            </Button>
          </section>

          {error ? (
            <div className="flex items-start gap-2 rounded-md bg-danger-soft/15 px-3 py-2 text-sm text-danger-soft-fg">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{error}</span>
            </div>
          ) : null}

          <div className="flex items-center justify-end gap-2">
            <Link to="/admin/kit-templates">
              <Button variant="secondary" size="md" type="button">
                Cancel
              </Button>
            </Link>
            <Button
              type="submit"
              variant="primary"
              size="md"
              iconLeft={<Save className="h-4 w-4" />}
              loading={pending}
              disabled={!targetIsComplete(target)}
            >
              {isEdit ? 'Save changes' : 'Create template'}
            </Button>
          </div>
        </form>
      </div>
    </main>
  );
}
